import { Suspense } from 'react';
import { lazyPage, withRoutePreload } from '../prerender/lazyPage';
import { Outlet, Navigate, useParams, type RouteObject } from 'react-router-dom';
import { MainLayout } from '../layouts/MainLayout';
import { ProtectedRoute } from '../components/routing/ProtectedRoute';
import { GuestRoute } from '../components/routing/GuestRoute';
import { RouteErrorBoundary } from '../components/routing/RouteErrorBoundary';

// Pages
import { HomePage } from '../pages/home/HomePage';
import { ErrorPage } from '../pages/error/ErrorPage';

// SEO-R2: route-level code splitting keeps non-homepage flows out of the homepage bundle.
const LoginPage = lazyPage(() => import('../pages/auth/LoginPage').then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazyPage(() => import('../pages/auth/RegisterPage').then((module) => ({ default: module.RegisterPage })));
const VerifyEmailPage = lazyPage(() => import('../pages/auth/VerifyEmailPage').then((module) => ({ default: module.VerifyEmailPage })));
const ForgotPasswordPage = lazyPage(() => import('../pages/auth/ForgotPasswordPage').then((module) => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazyPage(() => import('../pages/auth/ResetPasswordPage').then((module) => ({ default: module.ResetPasswordPage })));
const CreateJobWizard = lazyPage(() => import('../components/jobs/CreateJobWizard').then((module) => ({ default: module.CreateJobWizard })));
const MyJobsPage = lazyPage(() => import('../pages/jobs/MyJobsPage').then((module) => ({ default: module.MyJobsPage })));
const JobDetailPage = lazyPage(() => import('../pages/jobs/JobDetailPage').then((module) => ({ default: module.JobDetailPage })));
const ProviderOnboardingPage = lazyPage(() => import('../pages/provider/ProviderOnboardingPage').then((module) => ({ default: module.ProviderOnboardingPage })));
const ProviderVerificationPage = lazyPage(() => import('../pages/provider/ProviderVerificationPage').then((module) => ({ default: module.ProviderVerificationPage })));
const ProviderCertificationPage = lazyPage(() => import('../pages/provider/ProviderCertificationPage').then((module) => ({ default: module.ProviderCertificationPage })));
const JobFeedPage = lazyPage(() => import('../pages/provider/JobFeedPage').then((module) => ({ default: module.JobFeedPage })));
const SendQuotePage = lazyPage(() => import('../pages/provider/SendQuotePage').then((module) => ({ default: module.SendQuotePage })));
const ProviderQuotesPage = lazyPage(() => import('../pages/provider/ProviderQuotesPage').then((module) => ({ default: module.ProviderQuotesPage })));
const ProviderJobsPage = lazyPage(() => import('../pages/provider/ProviderJobsPage').then((module) => ({ default: module.ProviderJobsPage })));
const DesignSystemDemo = lazyPage(() => import('../pages/DesignSystemDemo').then((module) => ({ default: module.DesignSystemDemo })));
const MyQuotesPage = lazyPage(() => import('../pages/client/MyQuotesPage').then((module) => ({ default: module.MyQuotesPage })));
const MessagesPage = lazyPage(() => import('../pages/messages/MessagesPage').then((module) => ({ default: module.MessagesPage })));
const ContactPage = lazyPage(() => import('../pages/support/ContactPage').then((module) => ({ default: module.ContactPage })));
const SearchProvidersPage = lazyPage(() => import('../pages/search/SearchProvidersPage').then((module) => ({ default: module.SearchProvidersPage })));
const CategoryLandingPage = lazyPage(() => import('../pages/search/CategoryLandingPage').then((module) => ({ default: module.CategoryLandingPage })));
const CityLandingPage = lazyPage(() => import('../pages/search/CityLandingPage').then((module) => ({ default: module.CityLandingPage })));
const CityCategoryLandingPage = lazyPage(() => import('../pages/search/CityCategoryLandingPage').then((module) => ({ default: module.CityCategoryLandingPage })));
const ProviderProfilePage = lazyPage(() => import('../pages/search/ProviderProfilePage').then((module) => ({ default: module.ProviderProfilePage })));
const CreateReviewPage = lazyPage(() => import('../pages/reviews/CreateReviewPage').then((module) => ({ default: module.CreateReviewPage })));
const SettingsPage = lazyPage(() => import('../pages/settings/SettingsPage').then((module) => ({ default: module.SettingsPage })));
const TermsPage = lazyPage(() => import('../pages/legal/TermsPage').then((module) => ({ default: module.TermsPage })));
const DeleteAccountPage = lazyPage(() => import('../pages/legal/DeleteAccountPage').then((module) => ({ default: module.DeleteAccountPage })));
const PrivacyPage = lazyPage(() => import('../pages/legal/PrivacyPage').then((module) => ({ default: module.PrivacyPage })));
const AdminDashboardPage = lazyPage(() => import('../pages/admin/AdminDashboardPage').then((module) => ({ default: module.AdminDashboardPage })));
const AdminCertificationsPage = lazyPage(() => import('../pages/admin/AdminCertificationsPage').then((module) => ({ default: module.AdminCertificationsPage })));
const AdminReportsPage = lazyPage(() => import('../pages/admin/AdminReportsPage'));
const AdminUsersPage = lazyPage(() => import('../pages/admin/AdminUsersPage'));
const AdminJobsPage = lazyPage(() => import('../pages/admin/AdminJobsPage'));
const AdminQuotesPage = lazyPage(() => import('../pages/admin/AdminQuotesPage'));
const SupportDashboardPage = lazyPage(() => import('../pages/admin/SupportDashboardPage').then((module) => ({ default: module.SupportDashboardPage })));
const SupportTicketDetailsPage = lazyPage(() => import('../pages/admin/SupportTicketDetailsPage').then((module) => ({ default: module.SupportTicketDetailsPage })));
const AdminSupportSeedsPage = lazyPage(() => import('../pages/admin/AdminSupportSeedsPage'));
const AdminFeatureFlagsPage = lazyPage(() => import('../pages/admin/AdminFeatureFlagsPage'));
const AdminEmailsPage = lazyPage(() => import('../pages/admin/AdminEmailsPage'));
const HowItWorksPage = lazyPage(() => import('../pages/static/HowItWorksPage').then((module) => ({ default: module.HowItWorksPage })));
const SecurityPage = lazyPage(() => import('../pages/static/SecurityPage').then((module) => ({ default: module.SecurityPage })));
const NotificationsPage = lazyPage(() => import('../pages/notifications/NotificationsPage').then((module) => ({ default: module.NotificationsPage })));
const BlogListPage = lazyPage(() => import('../pages/blog/BlogListPage').then((module) => ({ default: module.BlogListPage })));
const BlogPostPage = lazyPage(() => import('../pages/blog/BlogPostPage').then((module) => ({ default: module.BlogPostPage })));
const DevenirPrestatairePage = lazyPage(() => import('../pages/provider/DevenirPrestatairePage').then((module) => ({ default: module.DevenirPrestatairePage })));
const TradeProviderLandingPage = lazyPage(() => import('../pages/provider/TradeProviderLandingPage').then((module) => ({ default: module.TradeProviderLandingPage })));
const CityTradeProviderLandingPage = lazyPage(() => import('../pages/provider/CityTradeProviderLandingPage').then((module) => ({ default: module.CityTradeProviderLandingPage })));
const GuidePostPage = lazyPage(() => import('../pages/guide/GuidePostPage').then((module) => ({ default: module.GuidePostPage })));
const GuidesIndexPage = lazyPage(() => import('../pages/guide/GuidesIndexPage').then((module) => ({ default: module.GuidesIndexPage })));
const PriceSimulatorPage = lazyPage(() => import('../pages/tools/PriceSimulatorPage').then((module) => ({ default: module.PriceSimulatorPage })));

const SuspenseOutlet = () => (
  <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
    <Outlet />
  </Suspense>
);

const LocalizedLayout = () => {
  const { lang } = useParams<{ lang: string }>();

  if (lang !== 'ar' && lang !== 'en' && lang !== 'darija' && lang !== 'darija-ar') {
    return <ErrorPage />;
  }

  return <MainLayout />;
};

const localizedSeoChildren = [
  { index: true, element: <HomePage /> },
  { path: 'services/:slug', element: <CategoryLandingPage /> },
  { path: 'pro/:city/:category', element: <CityCategoryLandingPage /> },
  { path: 'pro/:slug', element: <CityLandingPage /> },
  { path: 'providers/:id', element: <ProviderProfilePage /> },
  { path: 'terms', element: <TermsPage /> },
  { path: 'privacy', element: <PrivacyPage /> },
  { path: 'delete-account', element: <DeleteAccountPage /> },
  { path: 'contact', element: <ContactPage /> },
  { path: 'how-it-works', element: <HowItWorksPage /> },
  { path: 'security', element: <SecurityPage /> },
  { path: 'blog', element: <BlogListPage /> },
  { path: 'blog/:slug', element: <BlogPostPage /> },
  { path: 'simulateur-prix', element: <PriceSimulatorPage /> },
  { path: 'devenir-prestataire', element: <DevenirPrestatairePage /> },
  { path: 'devenir-prestataire/:trade/:city', element: <CityTradeProviderLandingPage /> },
  { path: 'devenir-prestataire/:trade', element: <TradeProviderLandingPage /> },
  { path: 'guide/:slug', element: <GuidePostPage /> },
  { path: 'guides', element: <GuidesIndexPage /> },
  // Search is noindex; localized routes still resolve for all five user languages.
  { path: 'search', element: <SearchProvidersPage /> },
];

export const routes: RouteObject[] = withRoutePreload([
  {
    path: '/',
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'admin',
        element: (
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MODERATOR']}>
            <AdminDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/certifications',
        element: (
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MODERATOR']}>
            <AdminCertificationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/reports',
        element: (
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MODERATOR']}>
            <AdminReportsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MODERATOR']}>
            <AdminUsersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/jobs',
        element: (
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MODERATOR']}>
            <AdminJobsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/quotes',
        element: (
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_MODERATOR']}>
            <AdminQuotesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/support',
        element: (
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_SUPPORT']}>
            <SupportDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/support/:ticketId',
        element: (
          <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_SUPPORT']}>
            <SupportTicketDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/support-seeds',
        element: (
          <ProtectedRoute requiredRole="ROLE_ADMIN">
            <AdminSupportSeedsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/features',
        element: (
          <ProtectedRoute requiredRole="ROLE_ADMIN">
            <AdminFeatureFlagsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/emails',
        element: (
          <ProtectedRoute requiredRole="ROLE_ADMIN">
            <AdminEmailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'design-system',
        element: <DesignSystemDemo />,
      },
      // Jobs - CreateJobWizard accessible to guests (shows auth modal on publish)
      {
        path: 'jobs/create',
        element: <CreateJobWizard />,
      },
      {
        path: 'jobs/edit/:id',
        element: (
          <ProtectedRoute>
            <CreateJobWizard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'jobs',
        element: <Navigate to="my" replace />,
      },
      {
        path: 'jobs/my',
        element: (
          <ProtectedRoute>
            <MyJobsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'jobs/:id',
        element: (
          <ProtectedRoute>
            <JobDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'my-quotes',
        element: (
          <ProtectedRoute>
            <MyQuotesPage />
          </ProtectedRoute>
        ),
      },
      // Messages
      {
        path: 'messages',
        element: (
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'messages/:id',
        element: (
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        ),
      },
      // Notifications
      {
        path: 'notifications',
        element: (
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        ),
      },
      // Search & Providers
      {
        path: 'search',
        element: <SearchProvidersPage />,
      },
      {
        path: 'services/:slug',
        element: <CategoryLandingPage />,
      },
      {
        path: 'pro/:city/:category',
        element: <CityCategoryLandingPage />,
      },
      {
        path: 'pro/:slug',
        element: <CityLandingPage />,
      },
      {
        path: 'providers/:id',
        element: <ProviderProfilePage />,
      },
      // Reviews
      {
        path: 'reviews/create/:jobId',
        element: (
          <ProtectedRoute>
            <CreateReviewPage />
          </ProtectedRoute>
        ),
      },
      // Profile - redirect to Settings
      {
        path: 'profile',
        element: <Navigate to="/settings" replace />,
      },
      // Settings
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      // Legal
      {
        path: 'terms',
        element: <TermsPage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPage />,
      },
      {
        path: 'delete-account',
        element: <DeleteAccountPage />,
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },
      {
        path: 'how-it-works',
        element: <HowItWorksPage />,
      },
      {
        path: 'security',
        element: <SecurityPage />,
      },
      // Blog — static content, no auth required
      {
        path: 'blog',
        element: <BlogListPage />,
      },
      {
        path: 'blog/:slug',
        element: <BlogPostPage />,
      },
      // Price simulator — public, guest-friendly SEO tool
      {
        path: 'simulateur-prix',
        element: <PriceSimulatorPage />,
      },
      // Provider landing page (public)
      {
        path: 'devenir-prestataire',
        element: <DevenirPrestatairePage />,
      },
      {
        path: 'devenir-prestataire/:trade/:city',
        element: <CityTradeProviderLandingPage />,
      },
      {
        path: 'devenir-prestataire/:trade',
        element: <TradeProviderLandingPage />,
      },
      {
        path: 'guide/:slug',
        element: <GuidePostPage />,
      },
      {
        path: 'guides',
        element: <GuidesIndexPage />,
      },
      // Provider routes
      {
        path: 'become-provider',
        element: <Navigate to="/devenir-prestataire" replace />,
      },
      {
        path: 'provider/onboarding',
        element: (
          <ProtectedRoute>
            <ProviderOnboardingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'provider/verification',
        element: (
          <ProtectedRoute>
            <ProviderVerificationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'provider/certification',
        element: (
          <ProtectedRoute requireProvider>
            <ProviderCertificationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'provider/dashboard',
        element: (
          <ProtectedRoute requireProvider>
            <JobFeedPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'provider/jobs/:jobId/quote',
        element: (
          <ProtectedRoute requireProvider>
            <SendQuotePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'provider/jobs/:id',
        element: (
          <ProtectedRoute requireProvider>
            <JobDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'provider/quotes',
        element: (
          <ProtectedRoute requireProvider>
            <ProviderQuotesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'provider/my-jobs',
        element: (
          <ProtectedRoute requireProvider>
            <ProviderJobsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'provider/settings',
        element: <Navigate to="/settings#business" replace />,
      },
    ],
  },
  {
    path: '/:lang',
    element: <LocalizedLayout />,
    children: localizedSeoChildren,
  },
  {
    path: '/auth',
    element: <SuspenseOutlet />,
    children: [
      {
        path: 'login',
        element: (
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        ),
      },
      {
        path: 'register',
        element: (
          <GuestRoute>
            <RegisterPage />
          </GuestRoute>
        ),
      },
      {
        path: 'verify-email',
        element: <VerifyEmailPage />,
      },
    ],
  },
  {
    path: '/register',
    element: (
      <GuestRoute>
        <RegisterPage />
      </GuestRoute>
    ),
  },
  {
    path: '/verify-email',
    element: <VerifyEmailPage />,
  },
  {
    path: '/login',
    element: (
      <GuestRoute>
        <LoginPage />
      </GuestRoute>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <GuestRoute>
        <ForgotPasswordPage />
      </GuestRoute>
    ),
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '*',
    element: <ErrorPage />,
  },
].map((route) => ({ ...route, errorElement: <RouteErrorBoundary /> })));
